// src/pages/sessions/components/LiveSession/LiveSessionMain.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from 'react-error-boundary';
import { 
  LiveKitRoom, 
  RoomAudioRenderer,
  useRoomContext,
  useParticipants,
  useConnectionState,
} from '@livekit/components-react';
import { ConnectionState, ConnectionQuality, RoomEvent } from 'livekit-client';
import '@livekit/components-styles';
import { toast } from 'sonner';
import throttle from 'lodash.throttle';

// Desktop Components
import VideoContainer from './VideoContainer';
import SessionHeader from './layout/SessionHeader';
import SessionBottomBar from './layout/SessionBottomBar';
import SessionChat from './Chat/SessionChat';
import PersonalChatInSession from './Chat/PersonalChatInSession';
import BreakoutRoomPanel from './breakout/BreakoutRoomPanel';
import EnhancedParticipantsList from './EnhancedParticipantsList';
import AudioContextKeepAlive from '@/components/AudioContextKeepAlive';
import SoundEffectsManager from '@/components/SoundEffectsManager';
import ConnectionBanner from './layout/ConnectionBanner';
import MicrophoneWarningBanner from './layout/MicrophoneWarningBanner';

// Phone Layout
import PhoneLayout from './mobile/PhoneLayout';

// Managers
import { useRoomConnectionManager } from './managers/RoomConnectionManager';
import { useMediaManager } from './managers/MediaManager';
import { useBreakoutRoomManager } from './managers/BreakoutRoomManager';
import { useWhiteboardManager, WhiteboardCanvas, WhiteboardBottomBar } from './whiteboard';

// Hooks & Services
import { useSessionWebSocket } from '@/hooks/useSessionWebSocket';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useSessionInitialization } from './hooks/useSessionInitialization';
import { useBreakoutRooms } from './hooks/useBreakoutRooms';
import { useMediaControls } from './hooks/useMediaControls';
import { useConnectionRecovery } from './hooks/useConnectionRecovery';
import { useSubscriptions } from './hooks/useSubscriptions';
import { useConnectionQuality } from './hooks/useConnectionQuality';
import { useMediaDiagnostics } from './hooks/useMediaDiagnostics';
import useSessionStore from '@/stores/sessionStore';
import useAuthStore from '@/stores/authStore';

// Utils
import perfMon from '@/utils/performanceMonitor';
import { CleanupTracker } from '@/utils/CleanupTracker';
import { isMacOS, getDeviceType } from '@/utils/platform';

// Pre-join device check
import PreJoinDeviceCheck from './PreJoinDeviceCheck';

// Mac screen share guide
import MacScreenShareGuide from './Controls/MacScreenShareGuide';

// Icons
import { 
  PlayIcon,
  AcademicCapIcon,
  UsersIcon,
  ChatBubbleLeftRightIcon,
  Squares2X2Icon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  ComputerDesktopIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

// Constants
const DEBUG = import.meta.env.DEV;

const STORAGE_KEYS = {
  SIDEBAR_COLLAPSED: 'session_sidebar_collapsed',
  SIDEBAR_WIDTH: 'session_sidebar_width',
  DOC_EMBED_HELP_SEEN: 'doc_embed_help_seen'
};

const MIN_SIDEBAR_WIDTH = 160;
const MAX_SIDEBAR_WIDTH = 320;

// Helper functions
const safeGet = (key, fallback) => {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
};

const safeSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {}
};

// Device type hook — stable for the session lifetime (never re-detects mid-session)
const useDeviceType = () => {
  const [deviceType] = useState(() => getDeviceType());
  return deviceType;
};

// Shown inside the room when camera was unavailable at join time
const CameraUnavailableBanner = () => {
  const { t } = useTranslation();
  const [isDismissed, setIsDismissed] = useState(false);
  if (isDismissed) return null;
  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-orange-600 text-white px-4 py-3 shadow-lg animate-in slide-in-from-top duration-300">
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <ExclamationTriangleIcon className="w-6 h-6 flex-shrink-0" />
          <div>
            <p className="font-medium">
              {t('liveSession.cameraUnavailable.title')}
            </p>
            <p className="text-sm opacity-90">
              {t('liveSession.cameraUnavailable.description')}
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsDismissed(true)}
          className="p-1.5 hover:bg-white/20 rounded-full transition-colors flex-shrink-0"
          aria-label={t('common.dismiss', 'Dismiss')}
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// Error Boundary Component
const SessionErrorFallback = ({ error, resetErrorBoundary }) => {
  const { t } = useTranslation();

  const handleGoBack = () => {
    window.location.href = '/dashboard';
  };
  
  useEffect(() => {
    console.error('Session error:', error);
  }, [error]);
  
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-900">
      <div className="text-center max-w-md">
        <ExclamationTriangleIcon className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">{t('liveSession.error.title')}</h2>
        <p className="text-gray-300 mb-4">
          {t('liveSession.error.description')}
        </p>
        <div className="bg-gray-800 rounded p-3 mb-4">
          <p className="text-sm text-gray-400 font-mono break-all">
            {error?.message || t('liveSession.error.unknown')}
          </p>
        </div>
        <div className="space-y-2">
          <button
            onClick={resetErrorBoundary}
            className="w-full px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700 transition-colors"
          >
            {t('liveSession.error.tryAgain')}
          </button>
          <button
            onClick={handleGoBack}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
          >
            {t('liveSession.error.backToDashboard')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Screen Share Indicator Component
const ScreenShareIndicator = ({ screenSharers, localParticipantIdentity, screenSharerNames }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  
  if (!screenSharers || screenSharers.length === 0) {
    return null;
  }
  
  const primarySharer = screenSharers[0];
  const isLocalSharing = primarySharer.isLocal || primarySharer.identity === localParticipantIdentity;
  
  const getSharerName = (sharer) => {
    if (!sharer) return t('videoGrid.someone');
    
    if (sharer.name && !sharer.name.startsWith('user-')) {
      return sharer.name;
    }
    
    if (screenSharerNames && screenSharerNames[sharer.identity]) {
      return screenSharerNames[sharer.identity];
    }
    
    const identityParts = sharer.identity?.split('-');
    if (identityParts && identityParts.length > 0 && !identityParts[0].startsWith('user')) {
      return identityParts[0];
    }
    
    return t('videoGrid.someone');
  };
  
  const primaryName = isLocalSharing ? t('videoGrid.you') : getSharerName(primarySharer);
  
  return (
    <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-30 max-w-md">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-3 rounded-lg shadow-xl">
        <div className="flex items-center gap-3">
          <ComputerDesktopIcon className="w-5 h-5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-semibold">
              {isLocalSharing 
                ? t('liveSession.screenShare.youSharing') 
                : t('liveSession.screenShare.otherSharing', { name: primaryName })}
            </div>
            {screenSharers.length > 1 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs text-blue-100 hover:text-white mt-1 flex items-center gap-1"
              >
                {t('liveSession.screenShare.moreCount', { count: screenSharers.length - 1 })}
                <ChevronDownIcon className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              </button>
            )}
          </div>
          <div className="w-2 h-2 bg-white rounded-full animate-pulse flex-shrink-0" />
        </div>
        
        {expanded && screenSharers.length > 1 && (
          <div className="mt-3 pt-3 border-t border-blue-500/30 space-y-1">
            {screenSharers.slice(1).map(sharer => (
              <div key={sharer.trackSid || sharer.identity} className="flex items-center gap-2 text-sm text-blue-100">
                <div className="w-1.5 h-1.5 bg-blue-200 rounded-full" />
                {(sharer.isLocal || sharer.identity === localParticipantIdentity) ? t('videoGrid.you') : getSharerName(sharer)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Room State Tracker Component
const RoomStateTracker = ({ 
  roomRef,
  isTeacherUser,
  handleConnectionStateChange,
  processPendingUpdates,
  initializeRoom,
  forceDisconnectExisting,
  loadTeacherBroadcastState,
  loadInitialRoomAssignment,
  loadTeacherStatesForStudent,
  loadTeacherBroadcastStatesFromAPI,
  applyAllOnce,
  stateRefs,
  savedVirtualRoomIdRef,
  preloadedRoomAssignment,
  onCameraFailed,
}) => {
  const room = useRoomContext();
  const connectionState = useConnectionState();
  
  useEffect(() => {
    roomRef.current = room;
  }, [room, roomRef]);

  useEffect(() => {
    if (!room || !onCameraFailed) return;

    const handleMediaDevicesError = (error) => {
      console.warn('[RoomStateTracker] MediaDevicesError:', error?.name, error?.message);
      if (error?.name === 'NotReadableError' || error?.name === 'AbortError' || error?.name === 'NotAllowedError') {
        onCameraFailed();
      }
    };

    room.on(RoomEvent.MediaDevicesError, handleMediaDevicesError);
    return () => {
      room.off(RoomEvent.MediaDevicesError, handleMediaDevicesError);
    };
  }, [room, onCameraFailed]);
  
  useEffect(() => {
    if (stateRefs.isProcessingConnection) {
      return;
    }
    
    if (stateRefs.lastConnectionState === connectionState) {
      return;
    }
    
    stateRefs.isProcessingConnection = true;
    stateRefs.lastConnectionState = connectionState;
    
    console.log('LiveKit connection state:', connectionState);
    
    try {
      handleConnectionStateChange(connectionState);
      
      if (connectionState === ConnectionState.Connected) {
        console.log('Room connected, processing updates');
        
        if (roomRef.current) {
          processPendingUpdates(roomRef.current);
        }
        
        if (!stateRefs.hasConnectedOnce) {
          stateRefs.hasConnectedOnce = true;
          console.log('First time connection detected');
          
          if (!isTeacherUser && preloadedRoomAssignment !== undefined && preloadedRoomAssignment !== null) {
            console.log('Setting savedVirtualRoomIdRef to preloaded assignment:', preloadedRoomAssignment);
            savedVirtualRoomIdRef.current = preloadedRoomAssignment;
          }
          
          if (!stateRefs.hasInitializedRoom && roomRef.current) {
            stateRefs.hasInitializedRoom = true;
            console.log('Initializing room for first time');
            
            // NOTE: Do NOT call forceDisconnectExisting() here.
            // At this point roomRef.current points to the room that JUST connected,
            // so forceDisconnectExisting would disconnect it, causing a reconnect cycle
            // that kills local audio/video tracks. On Windows, the rapid device
            // release-reacquire causes WASAPI to return a broken audio stream (silence),
            // which DTX then suppresses — making the mic appear dead to other participants.
            initializeRoom(roomRef.current);
          }
          
          if (isTeacherUser && !stateRefs.hasLoadedTeacherState) {
            stateRefs.hasLoadedTeacherState = true;
            console.debug('Loading teacher broadcast state (once)');
            loadTeacherBroadcastState();
          } else if (!isTeacherUser) {
            if (!stateRefs.hasLoadedTeacherStates) {
              stateRefs.hasLoadedTeacherStates = true;
              console.debug('Student loading teacher monitoring/broadcasting states');
              if (loadTeacherBroadcastStatesFromAPI) {
                loadTeacherBroadcastStatesFromAPI().then(() => {
                  if (loadTeacherStatesForStudent) {
                    loadTeacherStatesForStudent();
                  }
                });
              } else if (loadTeacherStatesForStudent) {
                loadTeacherStatesForStudent();
              }
            }
            
            if (!stateRefs.hasLoadedRoomAssignment) {
              stateRefs.hasLoadedRoomAssignment = true;
              if (preloadedRoomAssignment === undefined || preloadedRoomAssignment === null) {
                console.debug('Loading initial room assignment (once on first connect)');
                loadInitialRoomAssignment();
              } else {
                console.debug('Using preloaded room assignment, skipping API call');
                setTimeout(() => {
                  applyAllOnce(true);
                }, 100);
              }
            }
          }
          
          setTimeout(() => {
            applyAllOnce();
          }, 500);
        }
      } else if (connectionState === ConnectionState.Disconnected) {
        console.log('Room disconnected');
      } else if (connectionState === ConnectionState.Reconnecting) {
        console.log('Room reconnecting');
      }
    } finally {
      stateRefs.isProcessingConnection = false;
    }
  }, [
    connectionState,
    roomRef,
    isTeacherUser,
    handleConnectionStateChange,
    processPendingUpdates,
    initializeRoom,
    loadTeacherBroadcastState,
    loadInitialRoomAssignment,
    loadTeacherStatesForStudent,
    loadTeacherBroadcastStatesFromAPI,
    applyAllOnce,
    stateRefs,
    savedVirtualRoomIdRef,
    preloadedRoomAssignment
  ]);
  
  return null;
};

// Connection Quality Tracker
const ConnectionQualityTracker = ({ roomRef, roomConnected, setConnectionQuality, setIsConnectionEstablishing }) => {
  const { localQuality, isLocalConnectionLost, isLocalConnectionUnstable, isEstablishing } = useConnectionQuality({
    roomRef,
    roomConnected,
  });
  
  useEffect(() => {
    let storeQuality = 'excellent';
    switch (localQuality) {
      case ConnectionQuality.Excellent: storeQuality = 'excellent'; break;
      case ConnectionQuality.Good:      storeQuality = 'good';      break;
      case ConnectionQuality.Poor:      storeQuality = 'poor';      break;
      case ConnectionQuality.Lost:      storeQuality = 'lost';      break;
      default:                          storeQuality = 'excellent';
    }
    setConnectionQuality(storeQuality);
  }, [localQuality, setConnectionQuality]);
  
  useEffect(() => {
    setIsConnectionEstablishing(isEstablishing);
  }, [isEstablishing, setIsConnectionEstablishing]);
  
  const showBanner = isLocalConnectionLost || isLocalConnectionUnstable;
  
  return (
    <ConnectionBanner 
      quality={localQuality} 
      isVisible={showBanner}
      isUnstable={isLocalConnectionUnstable}
    />
  );
};

const LiveSessionMain = ({ onClose, institutionName, deviceType: forceDeviceType }) => {
  const { t } = useTranslation();
  
  // Device type — stable for session lifetime
  const detectedDeviceType = useDeviceType();
  const deviceType = forceDeviceType ?? detectedDeviceType;
  const isPhone   = deviceType === 'phone';
  const isTablet  = deviceType === 'tablet';
  // isDesktop = !isPhone && !isTablet (unused variable, but kept for clarity)

  // Viewport setup for phone/tablet — lock body scroll and set --vh custom property
  useEffect(() => {
    if (deviceType === 'desktop') return;

    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);
    
    // Add session-active class — CSS handles background color, overflow, and
    // position:fixed on html/body/#root so nothing shows around the edges.
    document.documentElement.classList.add('session-active');

    // Belt-and-suspenders: block touchmove on document unless the target
    // is inside a scrollable container
    const blockScroll = (e) => {
      const scrollable = e.target.closest(
        '[data-scrollable], .overflow-y-auto, .overflow-x-auto, .overflow-auto'
      );
      if (!scrollable) e.preventDefault();
    };
    document.addEventListener('touchmove', blockScroll, { passive: false });
    
    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
      document.documentElement.classList.remove('session-active');
      document.removeEventListener('touchmove', blockScroll);
    };
  }, [deviceType]);

  // Performance monitoring
  useEffect(() => {
    if (DEBUG) {
      perfMon.mark('LiveSessionMain-mount');
      perfMon.startMemoryMonitoring(10000);
    }
    return () => {
      if (DEBUG) {
        perfMon.mark('LiveSessionMain-unmount');
        perfMon.measure('LiveSessionMain-lifetime', 'LiveSessionMain-mount', 'LiveSessionMain-unmount');
        perfMon.stopMemoryMonitoring();
        perfMon.logReport();
      }
    };
  }, []);

  // Core hooks
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isTeacher } = useAuthStore();
  const { setIsConnected, setConnectionQuality, setIsConnectionEstablishing, reset, sessionSettings, updateSessionSettings } = useSessionStore();
  
  const returnPath = location.state?.from || '/dashboard';
  const isTeacherUser = isTeacher();
  
  const savedVirtualRoomIdRef = useRef(null);
  const roomRef = useRef(null);
  
  const cleanupTrackerRef = useRef(new CleanupTracker());
  const cleanupTracker = cleanupTrackerRef.current;
  const mountedRef = useRef(true);
  const reloadedRef = useRef(false);
  const cleanupInProgressRef = useRef(false);
  
  const stateRefs = useRef({
    hasConnectedOnce: false,
    lastConnectionState: null,
    isProcessingConnection: false,
    hasInitializedRoom: false,
    hasLoadedTeacherState: false,
    hasLoadedRoomAssignment: false,
    hasLoadedTeacherStates: false
  }).current;
  
  // State
  const [isLeavingSession, setIsLeavingSession] = useState(false);
  const [hideOwnVideo, setHideOwnVideo] = useState(false);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  
  // Device check state
  // Phone skips pre-join entirely for now (simplified phone pre-join is in PreJoinDeviceCheck)
  // Tablet and desktop go through full pre-join
  const [deviceCheckComplete, setDeviceCheckComplete] = useState(isPhone);
  const [selectedDevices, setSelectedDevices] = useState({
    audioDeviceId: undefined,
    videoDeviceId: undefined,
    cameraAvailable: true
  });
  const [isValidatingDevices, setIsValidatingDevices] = useState(!isPhone);
  const [cameraFailedInSession, setCameraFailedInSession] = useState(false);
  const handleCameraFailedInSession = useCallback(() => setCameraFailedInSession(true), []);
  
  // Track which path was taken for media diagnostics telemetry
  const joinPathRef = useRef(isPhone ? 'phone-skip' : null);

  // Validate remembered devices on mount (skip for phone)
  useEffect(() => {
    if (isPhone) {
      setIsValidatingDevices(false);
      return;
    }

    const validateRememberedDevices = async () => {
      const rememberChoice = localStorage.getItem('simpleclass_remember_device_choice') === 'true';
      const savedAudioDevice = localStorage.getItem('simpleclass_audio_device');
      const savedVideoDevice = localStorage.getItem('simpleclass_video_device');

      if (!rememberChoice || (!savedAudioDevice && !savedVideoDevice)) {
        console.log('[DeviceValidation] No remembered devices, showing PreJoinDeviceCheck');
        setIsValidatingDevices(false);
        return;
      }

      try {
        console.log('[DeviceValidation] Validating saved devices:', { savedAudioDevice, savedVideoDevice });
        
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        const videoInputs = devices.filter(d => d.kind === 'videoinput');

        const hasDeviceIds = devices.some(d => d.deviceId && d.deviceId !== '');

        if (!hasDeviceIds) {
          console.log('[DeviceValidation] No device permission, showing PreJoinDeviceCheck');
          setIsValidatingDevices(false);
          return;
        }

        const audioValid = !savedAudioDevice || audioInputs.some(d => d.deviceId === savedAudioDevice);
        const videoValid = !savedVideoDevice || videoInputs.some(d => d.deviceId === savedVideoDevice);

        if (audioValid && videoValid) {
          const cameraAvailable = true;
          console.log('[DeviceValidation] Devices valid, skipping PreJoinDeviceCheck', { cameraAvailable });
          joinPathRef.current = 'remembered-devices';
          setSelectedDevices({
            audioDeviceId: savedAudioDevice || undefined,
            videoDeviceId: savedVideoDevice || undefined,
            cameraAvailable
          });
          setDeviceCheckComplete(true);
        } else {
          console.warn('[DeviceValidation] Saved devices no longer available, clearing');
          if (!audioValid) localStorage.removeItem('simpleclass_audio_device');
          if (!videoValid) localStorage.removeItem('simpleclass_video_device');
        }
      } catch (error) {
        console.warn('[DeviceValidation] Device enumeration failed:', error);
      }

      setIsValidatingDevices(false);
    };

    validateRememberedDevices();
  }, [isPhone]);
  
  // UI State - Desktop/Tablet sidebar
  const [activePanel, setActivePanel] = useState('participants');
  const [activeChatTab, setActiveChatTab] = useState('session');
  const [unreadPersonalMessages, setUnreadPersonalMessages] = useState(0);
  const [unreadSessionMessages, setUnreadSessionMessages] = useState(0);
  const [chatMounted, setChatMounted] = useState(false);
  
  // Sidebar state — tablets start collapsed, desktop respects localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (isPhone) return true;
    if (isTablet) return true; // tablets start with sidebar collapsed
    return safeGet(STORAGE_KEYS.SIDEBAR_COLLAPSED, 'false') === 'true';
  });
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    return parseInt(safeGet(STORAGE_KEYS.SIDEBAR_WIDTH, '320'), 10);
  });
  const sidebarRef = useRef(null);
  const isResizingRef = useRef(false);

  // Track orientation changes for phone and tablet
  useEffect(() => {
    if (deviceType === 'desktop') return;

    const handleOrientationChange = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
      // Fire synthetic resize after the browser has finished reflowing —
      // WhiteboardCanvas uses ResizeObserver with a 50ms debounce, so 350ms
      // gives it time to remeasure at the final settled dimensions.
      setTimeout(() => window.dispatchEvent(new Event('resize')), 350);
    };

    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [deviceType]);

  // Initialize session
  const {
    token,
    loading,
    error,
    sessionData,
    waitingForTeacher,
    handleStartSession,
    preloadedRoomAssignment,
    preloadedRooms
  } = useSessionInitialization(sessionId, user, isAuthenticated, isTeacherUser);

  useEffect(() => {
    if (!isTeacherUser && preloadedRoomAssignment !== undefined && preloadedRoomAssignment !== null) {
      console.log('Setting savedVirtualRoomIdRef from preloadedRoomAssignment:', preloadedRoomAssignment);
      savedVirtualRoomIdRef.current = preloadedRoomAssignment;
    }
  }, [preloadedRoomAssignment, isTeacherUser]);

  const { 
    sessionStatus: wsSessionStatus, 
    roomAssignment: wsRoomAssignment,
    isConnected: wsConnected,
    mediaRestrictions
  } = useSessionWebSocket(sessionId);
  const { addMessageHandler, sendMessage: wsSendMessage } = useWebSocket();

  // Breakout rooms hook
  const {
    virtualRooms,
    currentVirtualRoom,
    participantVirtualRooms,
    teacherBroadcastRooms,
    teacherMonitorRooms,
    teacherMonitorSilentRooms,
    isAllBroadcasting,
    isAllMonitoring,
    isAllMonitoringSilent,
    loadVirtualRooms,
    handleCreateVirtualRoom,
    handleDeleteRoom,
    handleRenameRoom,
    handleAssignToVirtualRoom,
    toggleTeacherBroadcast,
    toggleTeacherMonitor,
    toggleTeacherMonitorSilent,
    toggleBroadcastAll,
    toggleMonitorAll,
    toggleMonitorSilentAll,
    setCurrentVirtualRoom,
    setTeacherBroadcastRooms,
    setTeacherMonitorRooms,
    setTeacherMonitorSilentRooms,
    setForceRefreshCallback
  } = useBreakoutRooms(sessionId, () => isTeacherUser, null);

  const applyingSubsRef = useRef(false);
  const applyTimeoutRef = useRef(null);
  const subscriptionsRef = useRef(null);
  
  const applyAllOnce = useCallback((instant = false) => {
    if (!mountedRef.current) return;
    
    if (instant) {
      if (applyingSubsRef.current) return;
      
      applyingSubsRef.current = true;
      console.log('Applying subscriptions INSTANTLY');
      
      const applyFn = subscriptionsRef.current?.applyAll;
      if (typeof applyFn === 'function') {
        Promise.resolve(applyFn())
          .catch(console.error)
          .finally(() => { applyingSubsRef.current = false; });
      } else {
        applyingSubsRef.current = false;
      }
      return;
    }
    
    if (applyTimeoutRef.current) {
      clearTimeout(applyTimeoutRef.current);
      applyTimeoutRef.current = null;
    }
    
    applyTimeoutRef.current = setTimeout(() => {
      if (!mountedRef.current || applyingSubsRef.current) return;
      
      applyingSubsRef.current = true;
      console.log('Applying subscriptions (debounced)');
      
      const applyFn = subscriptionsRef.current?.applyAll;
      if (typeof applyFn === 'function') {
        Promise.resolve(applyFn())
          .catch(console.error)
          .finally(() => { applyingSubsRef.current = false; });
      } else {
        applyingSubsRef.current = false;
      }
    }, 100);
  }, []);

  useEffect(() => {
    if (!isTeacherUser && currentVirtualRoom !== undefined) {
      const roomId = currentVirtualRoom?.id ?? null;
      console.log('[LiveSessionMain] Syncing currentVirtualRoom to savedVirtualRoomIdRef:', roomId);
      savedVirtualRoomIdRef.current = roomId;
      
      setTimeout(() => {
        applyAllOnce(true);
      }, 100);
    }
  }, [currentVirtualRoom, isTeacherUser, applyAllOnce]);

  // Room Connection Manager
  const {
    roomConnected,
    connectionState,
    localParticipantIdentity,
    initializeRoom,
    updateMetadata,
    processPendingUpdates,
    updateParticipantMetadata,
    handleConnectionStateChange,
    forceDisconnectExisting
  } = useRoomConnectionManager({
    sessionId,
    user,
    isTeacherUser,
    savedVirtualRoomIdRef,
    preloadedRoomAssignment,
    setTeacherBroadcastRooms,
    setTeacherMonitorRooms,
    teacherBroadcastRooms,
    teacherMonitorRooms,
    setVideoEnabled: () => {},
    setAudioEnabled: () => {},
    setIsConnected
  });

  // BreakoutRoomManager
  const {
    processRoomChange,
    loadInitialRoomAssignment,
    handleCreateIndividualRooms,
    loadTeacherBroadcastState,
    loadTeacherStatesForStudent,
    loadTeacherBroadcastStatesFromAPI,
    remoteTeacherStates,
    setRemoteTeacherStates
  } = useBreakoutRoomManager({
    sessionId,
    user,
    isTeacherUser,
    localParticipantIdentity,
    roomRef,
    roomConnected,
    wsConnected,
    wsSendMessage,
    addMessageHandler,
    updateMetadata,
    updateParticipantMetadata,
    virtualRooms,
    currentVirtualRoom,
    participantVirtualRooms,
    teacherBroadcastRooms,
    teacherMonitorRooms,
    teacherMonitorSilentRooms,
    loadVirtualRooms,
    setCurrentVirtualRoom,
    setTeacherBroadcastRooms,
    setTeacherMonitorRooms,
    setTeacherMonitorSilentRooms,
    applyAllOnce,
    cleanupTracker,
    savedVirtualRoomIdRef,
    sessionSettings
  });

  const {
    audioEnabled,
    videoEnabled,
    videoToggling,
    toggleAudio: baseToggleAudio,
    toggleVideo: baseToggleVideo,
    setAudioEnabled,
    setVideoEnabled,
    userMutedAudio,
    userMutedVideo,
    hasUserInteracted,
    updateMediaRestrictions,
    cameraLockEnabled,
    microphoneLockEnabled,
    handleToggleMediaLock,
    canToggleCamera,
    canToggleMicrophone
  } = useMediaControls(roomRef, isTeacherUser, sessionId, wsSendMessage);

  // Media diagnostics — monitors mic/camera health after publish,
  // POSTs summary to backend at 10s (appears in Platform Backend logs)
  useMediaDiagnostics({
    roomRef,
    roomConnected,
    sessionId,
    joinPath: joinPathRef.current,
    requestedDevices: selectedDevices,
  });

  // Whiteboard Manager
  const whiteboardManager = useWhiteboardManager({
    roomRef,
    roomConnected,
    isTeacherUser,
    localParticipantIdentity,
    currentVirtualRoom,
    teacherBroadcastRooms,
    remoteTeacherStates,
    sessionId,
  });

  const whiteboardCleanupRef = useRef(null);
  whiteboardCleanupRef.current = whiteboardManager.cleanup;

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (whiteboardCleanupRef.current) whiteboardCleanupRef.current();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const {
    screenSharers,
    isScreenSharing,
    toggleScreenShare,
    screenSharerNamesRef,
    embeddedDocument,
    documentSharer,
    toggleDocumentShare,
    GoogleDocModalComponent,
    layoutMode,
    showMacScreenGuide,
    setShowMacScreenGuide,
    proceedWithScreenShare
  } = useMediaManager({
    onCloseWhiteboard: whiteboardManager.closeWhiteboard,
    roomRef,
    sessionId,
    isTeacherUser,
    localParticipantIdentity,
    user,
    sessionSettings
  });

  // Subscriptions
  const subscriptions = useSubscriptions({
    roomRef,
    isTeacherUser,
    savedVirtualRoomIdRef,
    teacherBroadcastRooms,
    teacherMonitorRooms,
    teacherMonitorSilentRooms,
    participantVirtualRooms,
    remoteTeacherStates,
    setRemoteTeacherStates
  });
  
  const { gracePeriodParticipants } = subscriptions;
  subscriptionsRef.current = subscriptions;

  // Media toggle functions with lock checks
  const toggleAudio = useCallback(async () => {
    if (!roomRef.current) return;
    
    if (!isTeacherUser && microphoneLockEnabled && audioEnabled) {
      toast.warning(t('liveSession.media.microphoneLocked'));
      return;
    }

    if (!isTeacherUser && sessionSettings?.disableStudentAudio) {
      toast.error(t('liveSession.media.microphoneDisabled'));
      return;
    }
    
    try {
      await baseToggleAudio();
    } catch (error) {
      console.error('Audio toggle failed:', error);
      toast.error(t('liveSession.media.audioToggleFailed'));
    }
  }, [audioEnabled, isTeacherUser, microphoneLockEnabled, sessionSettings, baseToggleAudio, t]);

  const toggleVideo = useCallback(async () => {
    if (!roomRef.current) return;
    
    if (!isTeacherUser && cameraLockEnabled && videoEnabled) {
      toast.warning(t('liveSession.media.cameraLocked'));
      return;
    }

    if (!isTeacherUser && sessionSettings?.disableStudentVideo) {
      toast.error(t('liveSession.media.cameraDisabled'));
      return;
    }
    
    await baseToggleVideo();
  }, [baseToggleVideo, isTeacherUser, cameraLockEnabled, videoEnabled, sessionSettings, t]);

  // Camera rotation handler
  const handleRotateCamera = useCallback(async () => {
    if (!roomRef.current) return;
    
    try {
      const localParticipant = roomRef.current.localParticipant;
      const videoPublication = Array.from(localParticipant.videoTrackPublications.values()).find(
        pub => pub.source === 'camera'
      );
      
      if (videoPublication?.track) {
        const currentFacingMode = videoPublication.track.mediaStreamTrack?.getSettings().facingMode;
        const newFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';
        
        await videoPublication.track.restartTrack({
          facingMode: newFacingMode
        });
        
        toast.success(t('liveSession.media.cameraSwitched', { 
          camera: newFacingMode === 'user' ? t('liveSession.media.front') : t('liveSession.media.back')
        }));
      }
    } catch (error) {
      console.error('Failed to rotate camera:', error);
      toast.error(t('liveSession.media.cameraSwitchFailed'));
    }
  }, [t]);

  // Recovery Component
  const Recovery = () => {
    useConnectionRecovery({
      roomRef,
      roomConnected,
      isTeacherUser,
      sessionId,
      processRoomChange,
      applyAllSubscriptions: applyAllOnce,
    });
    return null;
  };

  // ParticipantsProvider — mounted for ALL device types (phone needs it too)
  const ParticipantsProvider = () => {
    const participants = useParticipants();
    useEffect(() => {
      window.__livekitParticipants = participants;
      return () => {
        delete window.__livekitParticipants;
      };
    }, [participants]);
    return null;
  };

  // Handle leave session
  const handleLeaveSession = useCallback(async () => {
    if (isLeavingSession || cleanupInProgressRef.current) return;
    
    perfMon.mark('leave-session-start');
    cleanupInProgressRef.current = true;
    setIsLeavingSession(true);
    
    if (applyTimeoutRef.current) {
      clearTimeout(applyTimeoutRef.current);
      applyTimeoutRef.current = null;
    }
    
    cleanupTracker.cleanup();
    
    if (whiteboardCleanupRef.current) {
      whiteboardCleanupRef.current();
    }
    
    if (roomRef.current) {
      try {
        roomRef.current.removeAllListeners();
        await roomRef.current.disconnect();
      } catch (error) {
        console.error('Error disconnecting from room:', error);
      }
      roomRef.current = null;
    }
    
    stateRefs.hasConnectedOnce = false;
    stateRefs.lastConnectionState = null;
    stateRefs.isProcessingConnection = false;
    stateRefs.hasInitializedRoom = false;
    stateRefs.hasLoadedTeacherState = false;
    stateRefs.hasLoadedRoomAssignment = false;
    stateRefs.hasLoadedTeacherStates = false;
    
    setIsConnected(false);
    reset();
    
    perfMon.mark('leave-session-end');
    perfMon.measure('leave-session', 'leave-session-start', 'leave-session-end');
    
    window.location.href = returnPath;
  }, [isLeavingSession, returnPath, setIsConnected, reset, cleanupTracker, stateRefs]);

  // Sidebar resize handlers (desktop/tablet only)
  const handleMouseMove = useMemo(() => {
    if (isPhone) return () => {};
    
    const throttled = throttle((e) => {
      if (!isResizingRef.current || !mountedRef.current) return;
      
      const containerWidth = window.innerWidth;
      const newWidth = containerWidth - e.clientX;
      
      setSidebarWidth(Math.max(MIN_SIDEBAR_WIDTH, Math.min(MAX_SIDEBAR_WIDTH, newWidth)));
    }, 16);
    
    cleanupTracker.addListener('mousemove-throttled', () => {
      throttled.cancel?.();
    });
    
    return throttled;
  }, [cleanupTracker, isPhone]);

  const handleMouseUp = useCallback(() => {
    isResizingRef.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    handleMouseMove.cancel?.();
  }, [handleMouseMove]);

  const handleMouseDown = useCallback((e) => {
    isResizingRef.current = true;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    cleanupTracker.addListener(`resize-${Date.now()}`, () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      handleMouseMove.cancel?.();
    });
    
    e.preventDefault();
  }, [handleMouseMove, handleMouseUp, cleanupTracker]);

  const handleChatTabChange = useCallback((tab) => {
    if (!mountedRef.current) return;
    setActiveChatTab(tab);
    if (tab === 'personal') {
      setUnreadPersonalMessages(0);
    } else if (tab === 'session') {
      setUnreadSessionMessages(0);
    }
  }, []);

  const getSessionTitle = () => {
    if (!sessionData) return t('liveSession.loading');
    
    const parts = [];
    if (sessionData.courseName || sessionData.course_name) {
      parts.push(sessionData.courseName || sessionData.course_name);
    }
    
    const sessionNum = sessionData.sessionNumber || sessionData.session_number;
    if (sessionNum) {
      parts.push(t('sessions.sessionNumber', { number: sessionNum }));
    }
    
    if (currentVirtualRoom) {
      parts.push(currentVirtualRoom.display_name || `${t('videoGrid.room')} ${currentVirtualRoom.room_number}`);
    }
    
    return parts.join(' • ');
  };

  // Effects
  useEffect(() => {
    window.__applySubscriptionsGlobal = applyAllOnce;
    setForceRefreshCallback(() => applyAllOnce);
    
    return () => {
      delete window.__applySubscriptionsGlobal;
      setForceRefreshCallback(null);
    };
  }, [applyAllOnce, setForceRefreshCallback]);

  useEffect(() => {
    if (!isPhone) {
      safeSet(STORAGE_KEYS.SIDEBAR_COLLAPSED, String(sidebarCollapsed));
    }
  }, [sidebarCollapsed, isPhone]);

  useEffect(() => {
    if (!isPhone) {
      safeSet(STORAGE_KEYS.SIDEBAR_WIDTH, String(sidebarWidth));
    }
  }, [sidebarWidth, isPhone]);

  useEffect(() => {
    if (roomConnected && !chatMounted && mountedRef.current && !isPhone) {
      cleanupTracker.setTimeout(() => {
        if (mountedRef.current) setChatMounted(true);
      }, 500);
    }
  }, [roomConnected, chatMounted, cleanupTracker, isPhone]);

  useEffect(() => {
    document.title = `${institutionName || 'School'} - ${t('liveSession.title')}`;
  }, [institutionName, t]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      
      stateRefs.hasConnectedOnce = false;
      stateRefs.lastConnectionState = null;
      stateRefs.isProcessingConnection = false;
      stateRefs.hasInitializedRoom = false;
      stateRefs.hasLoadedTeacherState = false;
      stateRefs.hasLoadedRoomAssignment = false;
      stateRefs.hasLoadedTeacherStates = false;
      
      if (applyTimeoutRef.current) {
        clearTimeout(applyTimeoutRef.current);
        applyTimeoutRef.current = null;
      }
      
      cleanupTracker.cleanup();
      setIsConnected(false);
      reset();
    };
  }, [setIsConnected, reset, cleanupTracker, stateRefs]);

  useEffect(() => {
    if (!waitingForTeacher || isTeacherUser || !mountedRef.current) return;
    
    if (wsSessionStatus === 'live' && !reloadedRef.current) {
      reloadedRef.current = true;
      window.location.reload();
    }
  }, [wsSessionStatus, waitingForTeacher, isTeacherUser]);

  const updateMediaRestrictionsRef = useRef(updateMediaRestrictions);
  useEffect(() => {
    updateMediaRestrictionsRef.current = updateMediaRestrictions;
  }, [updateMediaRestrictions]);

  useEffect(() => {
    if (mediaRestrictions) {
      console.log('🎯 LiveSessionMain calling updateMediaRestrictions with:', mediaRestrictions);
      updateMediaRestrictionsRef.current?.(mediaRestrictions);
    }
  }, [mediaRestrictions]);

  const totalUnreadMessages = unreadPersonalMessages + unreadSessionMessages;
  
  const toggleHideOwnVideo = useCallback(() => {
    setHideOwnVideo(prev => !prev);
    toast.info(hideOwnVideo 
      ? t('liveSession.media.cameraVisible') 
      : t('liveSession.media.cameraHidden')
    );
  }, [hideOwnVideo, t]);

  const handleNavigateBack = useCallback(() => {
    window.location.href = returnPath;
  }, [returnPath]);

  const handleDeviceCheckComplete = useCallback((audioDeviceId, videoDeviceId, cameraAvailable = true) => {
    joinPathRef.current = 'prejoin';
    setSelectedDevices({ audioDeviceId, videoDeviceId, cameraAvailable });
    // Small delay to let the OS fully release audio devices after PreJoinDeviceCheck
    // stops its streams. Windows WASAPI needs time to close the device handle;
    // without this, LiveKit's getUserMedia can get a broken/silent audio track.
    setTimeout(() => setDeviceCheckComplete(true), 300);
  }, []);

  // ─── Render states ────────────────────────────────────────────────────────

  if (isLeavingSession) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white">{t('liveSession.leaving')}</h2>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white">{t('liveSession.loadingSession')}</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-red-400 mb-4">{t('liveSession.error.title')}</h2>
          <p className="text-gray-300 mb-6">{error}</p>
          <button
            onClick={handleNavigateBack}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            {t('liveSession.goBack')}
          </button>
        </div>
      </div>
    );
  }

  if (sessionData && (sessionData.status === 'scheduled' || sessionData.session_status === 'scheduled') && isTeacherUser && !token) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-900">
        <div className="text-center">
          <AcademicCapIcon className="w-16 h-16 text-cyan-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">{t('liveSession.readyToStart.title')}</h2>
          <p className="text-gray-300 mb-4">{getSessionTitle()}</p>
          <p className="text-gray-400 mb-6">{t('liveSession.readyToStart.description')}</p>
          
          <button
            onClick={handleStartSession}
            disabled={loading}
            className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 text-white rounded-lg flex items-center space-x-2 mx-auto transition-colors"
          >
            <PlayIcon className="w-5 h-5" />
            <span>{loading ? t('liveSession.readyToStart.starting') : t('liveSession.readyToStart.startButton')}</span>
          </button>
          
          <button
            onClick={handleNavigateBack}
            className="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
          >
            {t('liveSession.goBack')}
          </button>
        </div>
      </div>
    );
  }

  if (waitingForTeacher && !isTeacherUser) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-900">
        <div className="text-center">
          <div className="text-yellow-400 text-6xl mb-4">⏰</div>
          <h2 className="text-xl font-semibold text-white mb-2">{t('liveSession.waiting.title')}</h2>
          <p className="text-gray-300 mb-4">{getSessionTitle()}</p>
          <p className="text-gray-400 mb-6">{t('liveSession.waiting.description')}</p>
          <div className="animate-pulse text-gray-500 mb-6">
            {wsConnected ? t('liveSession.waiting.waitingForTeacher') : t('liveSession.connecting')}
          </div>
          
          <button
            onClick={handleNavigateBack}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
          >
            {t('liveSession.goBack')}
          </button>
        </div>
      </div>
    );
  }

  if (token && sessionData) {
    if (isValidatingDevices) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-900">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white">{t('liveSession.checkingDevices', 'Checking your devices...')}</h2>
          </div>
        </div>
      );
    }

    if (!deviceCheckComplete) {
      return (
        <PreJoinDeviceCheck
          sessionData={sessionData}
          deviceType={deviceType}
          onComplete={handleDeviceCheckComplete}
          onCancel={handleNavigateBack}
        />
      );
    }

    // Whiteboard component — built once here, passed to both phone and desktop paths
    const whiteboardComponent = whiteboardManager.isWhiteboardActive ? (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-hidden">
          <WhiteboardCanvas
            fabricRef={whiteboardManager.fabricCanvasRef}
            activeTool={whiteboardManager.activeTool}
            activeColor={whiteboardManager.activeColor}
            brushSize={whiteboardManager.brushSize}
            canDraw={whiteboardManager.canDraw}
            isTeacher={isTeacherUser}
            onPathCreated={whiteboardManager.handlePathCreated}
            onObjectModified={whiteboardManager.handleObjectModified}
            onSelectionChange={whiteboardManager.handleSelectionChange}
            onStrokeStart={whiteboardManager.handleStrokeStart}
            onStrokePoint={whiteboardManager.handleStrokePoint}
            onStrokeEnd={whiteboardManager.handleStrokeEnd}
            onShapeCreated={whiteboardManager.handleShapeCreated}
            onTextCreated={whiteboardManager.handleTextCreated}
            onObjectErased={whiteboardManager.handleObjectErased}
            onToolChange={whiteboardManager.setActiveTool}
            onDeleteSelected={whiteboardManager.deleteSelected}
            onAddImage={whiteboardManager.handleAddImage}
          />
        </div>
        <WhiteboardBottomBar
          activeTool={whiteboardManager.activeTool}
          toolSettings={whiteboardManager.toolSettings}
          isTeacher={isTeacherUser}
          canDraw={whiteboardManager.canDraw}
          studentDrawingEnabled={whiteboardManager.studentDrawingEnabled}
          onToolChange={whiteboardManager.setActiveTool}
          onToolSettingsChange={whiteboardManager.updateToolSettings}
          onClear={whiteboardManager.clearCanvas}
          onClose={whiteboardManager.closeWhiteboard}
          onToggleStudentDrawing={whiteboardManager.toggleStudentDrawing}
          isDetached={whiteboardManager.isDetached}
          onDetach={whiteboardManager.detachWhiteboard}
          onAttach={whiteboardManager.attachWhiteboard}
        />
      </div>
    ) : null;

    return (
      <ErrorBoundary
        FallbackComponent={SessionErrorFallback}
        onReset={() => window.location.reload()}
      >
        <LiveKitRoom
          video={selectedDevices.cameraAvailable === false
            ? false
            : selectedDevices.videoDeviceId 
              ? { deviceId: { exact: selectedDevices.videoDeviceId } } 
              : true}
          audio={selectedDevices.audioDeviceId 
            ? { deviceId: { exact: selectedDevices.audioDeviceId } } 
            : true}
          token={token}
          serverUrl={import.meta.env.VITE_LIVEKIT_URL || 'wss://media.simpleclass.eu'}
          connectOptions={{
            autoSubscribe: false,
            publishOnConnect: true,
            maxRetries: 5,
            peerConnectionTimeout: 45000,
            publishDefaults: {
              audioPreset: 'speech',
              dtx: true,
              red: true,
              forceStereo: false,
              audioBitrate: 48_000,
              videoCodec: 'vp8',
              videoEnabled: true,
              audioEnabled: true,
              degradationPreference: 'maintain-resolution',
            },
          }}
          options={{
            exposeParticipant: true,
            disconnectOnPageLeave: true,
            stopLocalTrackOnUnpublish: true,
            adaptiveStream: true,
            dynacast: true,
            audioCaptureDefaults: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              channelCount: 1,
              sampleRate: 48000,
              ...(selectedDevices.audioDeviceId && { deviceId: selectedDevices.audioDeviceId }),
            },
            publishDefaults: {
              screenShareEncoding: { maxBitrate: 1_500_000, maxFramerate: 15 },
              videoSimulcastLayers: [
                { width: 320, height: 180, encoding: { maxBitrate: 150_000, maxFramerate: 15 } },
                { width: 640, height: 360, encoding: { maxBitrate: 400_000, maxFramerate: 24 } },
                { width: 1280, height: 720, encoding: { maxBitrate: 1_500_000, maxFramerate: 30 } },
              ],
              audioPreset: 'speech',
              dtx: true,
              red: true,
            },
            // Phones get reduced video capture, tablets and desktop get full quality
            videoCaptureDefaults: isPhone ? {
              facingMode: 'user',
              resolution: { width: 640, height: 480 }
            } : {
              ...(selectedDevices.videoDeviceId && { deviceId: selectedDevices.videoDeviceId }),
            },
          }}
        >
          <RoomStateTracker 
            roomRef={roomRef}
            isTeacherUser={isTeacherUser}
            handleConnectionStateChange={handleConnectionStateChange}
            processPendingUpdates={processPendingUpdates}
            initializeRoom={initializeRoom}
            forceDisconnectExisting={forceDisconnectExisting}
            loadTeacherBroadcastState={loadTeacherBroadcastState}
            loadInitialRoomAssignment={loadInitialRoomAssignment}
            loadTeacherStatesForStudent={loadTeacherStatesForStudent}
            loadTeacherBroadcastStatesFromAPI={loadTeacherBroadcastStatesFromAPI}
            applyAllOnce={applyAllOnce}
            stateRefs={stateRefs}
            savedVirtualRoomIdRef={savedVirtualRoomIdRef}
            preloadedRoomAssignment={preloadedRoomAssignment}
            onCameraFailed={handleCameraFailedInSession}
          />
          <Recovery />
          <ConnectionQualityTracker 
            roomRef={roomRef}
            roomConnected={roomConnected}
            setConnectionQuality={setConnectionQuality}
            setIsConnectionEstablishing={setIsConnectionEstablishing}
          />
          {/* ParticipantsProvider — mounted for ALL device types */}
          <ParticipantsProvider />
          <AudioContextKeepAlive />
          <SoundEffectsManager />
          <MicrophoneWarningBanner roomConnected={roomConnected} />
          {(selectedDevices.cameraAvailable === false || cameraFailedInSession) && (
            <CameraUnavailableBanner />
          )}
          
          {/* ── PHONE LAYOUT ─────────────────────────────────────────────── */}
          {isPhone ? (
            <PhoneLayout
              sessionId={sessionId}
              currentUser={user}
              isTeacher={isTeacherUser}
              screenSharers={screenSharers}
              embeddedDocument={embeddedDocument}
              documentSharer={documentSharer}
              localParticipantIdentity={localParticipantIdentity}
              virtualRooms={virtualRooms}
              participantVirtualRooms={participantVirtualRooms}
              teacherBroadcastRooms={teacherBroadcastRooms}
              teacherMonitorRooms={teacherMonitorRooms}
              teacherMonitorSilentRooms={teacherMonitorSilentRooms}
              remoteTeacherStates={remoteTeacherStates}
              currentVirtualRoom={currentVirtualRoom}
              hideOwnVideo={hideOwnVideo}
              gracePeriodParticipants={gracePeriodParticipants}
              isWhiteboardActive={whiteboardManager.isWhiteboardActive}
              isWhiteboardDetached={whiteboardManager.isDetached}
              whiteboardManager={whiteboardManager}
              audioEnabled={audioEnabled}
              videoEnabled={videoEnabled}
              userMutedAudio={userMutedAudio}
              userMutedVideo={userMutedVideo}
              hasUserInteracted={hasUserInteracted}
              canToggleMicrophone={canToggleMicrophone}
              canToggleCamera={canToggleCamera}
              onToggleAudio={toggleAudio}
              onToggleVideo={toggleVideo}
              onRotateCamera={handleRotateCamera}
              onLeave={handleLeaveSession}
              isAllBroadcasting={isAllBroadcasting}
              isAllMonitoring={isAllMonitoring}
              isAllMonitoringSilent={isAllMonitoringSilent}
              onCreateRoom={handleCreateVirtualRoom}
              onDeleteRoom={handleDeleteRoom}
              onRenameRoom={handleRenameRoom}
              onAssignParticipant={handleAssignToVirtualRoom}
              onToggleBroadcast={toggleTeacherBroadcast}
              onToggleMonitor={toggleTeacherMonitor}
              onToggleMonitorSilent={toggleTeacherMonitorSilent}
              onToggleAllBroadcast={toggleBroadcastAll}
              onToggleAllMonitor={toggleMonitorAll}
              onToggleAllMonitorSilent={toggleMonitorSilentAll}
              onCreateIndividualRooms={handleCreateIndividualRooms}
            />
          ) : (
          // ── DESKTOP / TABLET LAYOUT ────────────────────────────────────
            <div
              className="w-full flex flex-col bg-gray-900 overflow-hidden"
              style={{
                height: '100dvh',
                maxHeight: '100dvh',
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }}
            >
              <SessionHeader 
                sessionData={sessionData}
                currentVirtualRoom={currentVirtualRoom}
                roomConnected={roomConnected}
                onLeave={handleLeaveSession}
                isTablet={isTablet}
              />

              <div className="flex-1 flex relative overflow-hidden">
                <div className={`flex-1 relative bg-gray-900 transition-all duration-300 ${sidebarCollapsed ? 'w-full' : ''}`}>
                  {connectionState === ConnectionState.Connecting && !roomConnected ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="flex flex-col items-center">
                        <div className="text-white text-xl mb-4">{t('liveSession.connecting')}</div>
                        <div className="flex gap-3">
                          <div className="w-4 h-4 bg-cyan-500 rounded-full animate-pulse" />
                          <div className="w-4 h-4 bg-cyan-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                          <div className="w-4 h-4 bg-cyan-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <VideoContainer 
                      layoutMode={layoutMode}
                      screenSharers={screenSharers}
                      embeddedDocument={embeddedDocument}
                      documentSharer={documentSharer}
                      localParticipantIdentity={localParticipantIdentity}
                      virtualRooms={virtualRooms}
                      participantVirtualRooms={participantVirtualRooms}
                      teacherBroadcastRooms={teacherBroadcastRooms}
                      teacherMonitorRooms={teacherMonitorRooms}
                      teacherMonitorSilentRooms={teacherMonitorSilentRooms}
                      remoteTeacherStates={remoteTeacherStates}
                      currentVirtualRoom={currentVirtualRoom}
                      isTeacher={isTeacherUser}
                      hideOwnVideo={hideOwnVideo}
                      gracePeriodParticipants={gracePeriodParticipants}
                      isWhiteboardActive={whiteboardManager.isWhiteboardActive}
                      isWhiteboardDetached={whiteboardManager.isDetached}
                      whiteboardComponent={whiteboardComponent}
                    />
                  )}
                  
                  <ScreenShareIndicator 
                    screenSharers={screenSharers} 
                    localParticipantIdentity={localParticipantIdentity}
                    screenSharerNames={screenSharerNamesRef.current}
                  />
                </div>
                
                <div 
                  ref={sidebarRef}
                  className={`bg-gray-800 border-l border-gray-700 flex flex-col relative transition-all duration-300 ${
                    sidebarCollapsed ? 'w-0 overflow-hidden' : ''
                  }`}
                  style={{ 
                    width: sidebarCollapsed ? 0 : `${sidebarWidth}px`,
                    minWidth: sidebarCollapsed ? 0 : `${MIN_SIDEBAR_WIDTH}px`,
                    maxWidth: sidebarCollapsed ? 0 : `${MAX_SIDEBAR_WIDTH}px`
                  }}
                >
                  {!sidebarCollapsed && (
                    <div
                      className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-cyan-500 transition-colors z-10"
                      onMouseDown={handleMouseDown}
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={t('liveSession.sidebar.resizeLabel')}
                      tabIndex={0}
                      style={{ touchAction: 'none' }}
                    />
                  )}
                  
                  <div className={`flex flex-col h-full ${sidebarCollapsed ? 'hidden' : ''}`}>
                    <div className="h-12 flex border-b border-gray-700 flex-shrink-0">
                      <button
                        onClick={() => setActivePanel('participants')}
                        className={`flex-1 px-3 text-sm font-medium transition-colors ${
                          activePanel === 'participants'
                            ? 'bg-gray-700 text-white'
                            : 'bg-gray-800 text-gray-400 hover:text-white'
                        }`}
                      >
                        <UsersIcon className="w-4 h-4 inline mr-2" />
                        <span className={sidebarWidth < 200 ? 'hidden' : 'inline'}>{t('liveSession.sidebar.participants')}</span>
                      </button>
                      <button
                        onClick={() => setActivePanel('chat')}
                        className={`flex-1 px-3 text-sm font-medium transition-colors relative ${
                          activePanel === 'chat'
                            ? 'bg-gray-700 text-white'
                            : 'bg-gray-800 text-gray-400 hover:text-white'
                        }`}
                      >
                        <ChatBubbleLeftRightIcon className="w-4 h-4 inline mr-2" />
                        <span className={sidebarWidth < 200 ? 'hidden' : 'inline'}>{t('liveSession.sidebar.chat')}</span>
                        {activePanel !== 'chat' && totalUnreadMessages > 0 && (
                          <span className="absolute top-2 right-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full animate-pulse">
                            {totalUnreadMessages}
                          </span>
                        )}
                      </button>
                      {isTeacherUser && (
                        <button
                          onClick={() => setActivePanel('breakout')}
                          className={`flex-1 px-3 text-sm font-medium transition-colors ${
                            activePanel === 'breakout'
                              ? 'bg-gray-700 text-white'
                              : 'bg-gray-800 text-gray-400 hover:text-white'
                          }`}
                        >
                          <Squares2X2Icon className="w-4 h-4 inline mr-2" />
                          <span className={sidebarWidth < 200 ? 'hidden' : 'inline'}>{t('liveSession.sidebar.rooms')}</span>
                        </button>
                      )}
                    </div>
                    
                    <div className="flex-1 relative overflow-hidden">
                      <div className={`absolute inset-0 ${activePanel === 'participants' ? 'block' : 'hidden'}`}>
                        <EnhancedParticipantsList 
                          participantVirtualRooms={participantVirtualRooms}
                          virtualRooms={virtualRooms}
                          teacherBroadcastRooms={teacherBroadcastRooms}
                          teacherMonitorRooms={teacherMonitorRooms}
                          teacherMonitorSilentRooms={teacherMonitorSilentRooms}
                          isTeacher={isTeacherUser}
                        />
                      </div>
                      
                      <div className={`absolute inset-0 flex flex-col ${activePanel === 'chat' ? 'block' : 'hidden'}`}>
                        <div className="h-12 flex border-b border-gray-700 flex-shrink-0">
                          <button
                            onClick={() => handleChatTabChange('session')}
                            className={`flex-1 px-3 text-sm font-medium relative ${
                              activeChatTab === 'session'
                                ? 'bg-gray-700 text-white border-b-2 border-cyan-500'
                                : 'bg-gray-800 text-gray-400 hover:text-white'
                            }`}
                          >
                            {t('liveSession.sidebar.sessionChat')}
                            {activeChatTab !== 'session' && unreadSessionMessages > 0 && (
                              <span className="absolute top-2 right-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full animate-pulse">
                                {unreadSessionMessages}
                              </span>
                            )}
                          </button>
                          <button
                            onClick={() => handleChatTabChange('personal')}
                            className={`flex-1 px-3 text-sm font-medium relative ${
                              activeChatTab === 'personal'
                                ? 'bg-gray-700 text-white border-b-2 border-cyan-500'
                                : 'bg-gray-800 text-gray-400 hover:text-white'
                            }`}
                          >
                            {t('liveSession.sidebar.personalMessages')}
                            {activeChatTab !== 'personal' && unreadPersonalMessages > 0 && (
                              <span className="absolute top-2 right-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full animate-pulse">
                                {unreadPersonalMessages}
                              </span>
                            )}
                          </button>
                        </div>
                        
                        <div className="flex-1 relative overflow-hidden">
                          {chatMounted && (
                            <>
                              <div className={`absolute inset-0 ${activeChatTab === 'session' ? 'block' : 'hidden'}`}>
                                <SessionChat 
                                  sessionId={sessionId}
                                  onUnreadCountChange={setUnreadSessionMessages}
                                  isActive={activeChatTab === 'session' && activePanel === 'chat'}
                                />
                              </div>
                              <div className={`absolute inset-0 ${activeChatTab === 'personal' ? 'block' : 'hidden'}`}>
                                <PersonalChatInSession
                                  isOpen={true}
                                  sessionId={sessionId}
                                  currentUser={user}
                                  onClose={() => setActivePanel('participants')}
                                  onUnreadCountChange={setUnreadPersonalMessages}
                                  isActive={activeChatTab === 'personal' && activePanel === 'chat'}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {isTeacherUser && (
                        <div className={`absolute inset-0 ${activePanel === 'breakout' ? 'block' : 'hidden'}`}>
                          <BreakoutRoomPanel
                            virtualRooms={virtualRooms}
                            participantVirtualRooms={participantVirtualRooms}
                            teacherBroadcastRooms={teacherBroadcastRooms}
                            teacherMonitorRooms={teacherMonitorRooms}
                            teacherMonitorSilentRooms={teacherMonitorSilentRooms}
                            isAllBroadcasting={isAllBroadcasting}
                            isAllMonitoring={isAllMonitoring}
                            isAllMonitoringSilent={isAllMonitoringSilent}
                            onCreateRoom={handleCreateVirtualRoom}
                            onDeleteRoom={handleDeleteRoom}
                            onRenameRoom={handleRenameRoom}
                            onAssignParticipant={handleAssignToVirtualRoom}
                            onToggleBroadcast={toggleTeacherBroadcast}
                            onToggleMonitor={toggleTeacherMonitor}
                            onToggleMonitorSilent={toggleTeacherMonitorSilent}
                            onToggleAllBroadcast={toggleBroadcastAll}
                            onToggleAllMonitor={toggleMonitorAll}
                            onToggleAllMonitorSilent={toggleMonitorSilentAll}
                            onCreateIndividualRooms={handleCreateIndividualRooms}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <SessionBottomBar
                audioEnabled={audioEnabled}
                videoEnabled={videoEnabled}
                screenSharing={isScreenSharing}
                embeddedDocument={embeddedDocument}
                isWhiteboardActive={whiteboardManager.isWhiteboardActive}
                isWhiteboardDetached={whiteboardManager.isDetached}
                videoToggling={videoToggling}
                canToggleCamera={canToggleCamera}
                canToggleMicrophone={canToggleMicrophone}
                cameraLockEnabled={cameraLockEnabled}
                microphoneLockEnabled={microphoneLockEnabled}
                hideOwnVideo={hideOwnVideo}
                isTeacher={isTeacherUser}
                sidebarCollapsed={sidebarCollapsed}
                roomConnected={roomConnected}
                onToggleAudio={toggleAudio}
                onToggleVideo={toggleVideo}
                onToggleScreenShare={toggleScreenShare}
                onToggleDocumentShare={toggleDocumentShare}
                onToggleWhiteboard={() => {
                  if (whiteboardManager.isWhiteboardActive) {
                    if (whiteboardManager.isDetached) {
                      whiteboardManager.attachWhiteboard();
                    } else {
                      whiteboardManager.closeWhiteboard();
                    }
                  } else {
                    whiteboardManager.openWhiteboard();
                  }
                }}
                onToggleMediaLock={handleToggleMediaLock}
                onToggleHideOwnVideo={toggleHideOwnVideo}
                onToggleSidebar={() => {
                  setSidebarCollapsed(prev => !prev);
                  // Dispatch resize after the CSS transition (300ms) so whiteboard
                  // canvas and other sized elements reflow to the new container size.
                  setTimeout(() => window.dispatchEvent(new Event('resize')), 320);
                }}
                userMutedAudio={userMutedAudio}
                userMutedVideo={userMutedVideo}
                hasUserInteracted={hasUserInteracted}
                isTablet={isTablet}
                unreadMessages={totalUnreadMessages}
                autoAssignBreakoutRooms={sessionSettings?.autoAssignBreakoutRooms || false}
                onToggleAutoAssignBreakoutRooms={() => updateSessionSettings({ autoAssignBreakoutRooms: !sessionSettings?.autoAssignBreakoutRooms })}
              />
              
              {GoogleDocModalComponent && GoogleDocModalComponent()}
              
              <MacScreenShareGuide
                isOpen={showMacScreenGuide}
                onClose={() => setShowMacScreenGuide(false)}
                onContinue={proceedWithScreenShare}
              />
              
              <RoomAudioRenderer />
            </div>
          )}
        </LiveKitRoom>
      </ErrorBoundary>
    );
  }

  return null;
};

export default LiveSessionMain;
